/**
 * DraggableFab — Generic draggable Floating Action Button wrapper.
 *
 * Makes any FAB button draggable via pointer events.
 * - Persists position to localStorage via `storageKey`
 * - Restores position on mount
 * - Distinguishes drag vs click (drag distance < 5px triggers onClick)
 * - Clamps position within viewport bounds (8px margin)
 * - Shows grab cursor; grabbing cursor during drag
 *
 * Props:
 *   children        {ReactNode}  FAB button content
 *   defaultPosition {object}     { bottom, right } — default: { bottom: 24, right: 24 }
 *   storageKey      {string}     localStorage key for position persistence
 *   className       {string}     Extra class name
 *   style           {object}     Extra inline styles
 *   onClick         {function}   Called only when drag distance < 5px (i.e. it's a click)
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';

const DEFAULT_POSITION = { bottom: 24, right: 24 };
const DRAG_THRESHOLD = 5; // px — below this, pointer up is treated as a click
const MARGIN = 8; // px — minimum distance from viewport edges

function loadPosition(storageKey, defaultPosition) {
  if (!storageKey) return defaultPosition;
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (typeof parsed.bottom === 'number' && typeof parsed.right === 'number') {
        return parsed;
      }
    }
  } catch (_) {}
  return defaultPosition;
}

function clampPosition(bottom, right) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  return {
    bottom: Math.max(MARGIN, Math.min(vh - MARGIN - 52, bottom)),
    right: Math.max(MARGIN, Math.min(vw - MARGIN - 52, right)),
  };
}

export function DraggableFab({
  children,
  defaultPosition = DEFAULT_POSITION,
  storageKey,
  className,
  style,
  onClick,
  ...rest
}) {
  const [position, setPosition] = useState(() =>
    loadPosition(storageKey, defaultPosition)
  );
  const [dragging, setDragging] = useState(false);

  const dragState = useRef(null); // { startX, startY, startBottom, startRight, moved }

  // Clamp to viewport on mount and window resize
  useEffect(() => {
    const clamp = () => {
      setPosition((pos) => clampPosition(pos.bottom, pos.right));
    };
    window.addEventListener('resize', clamp);
    return () => window.removeEventListener('resize', clamp);
  }, []);

  const handlePointerDown = useCallback((e) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      startBottom: position.bottom,
      startRight: position.right,
      moved: false,
    };
    setDragging(true);
  }, [position]);

  const handlePointerMove = useCallback((e) => {
    if (!dragState.current) return;
    const dx = e.clientX - dragState.current.startX;
    const dy = e.clientY - dragState.current.startY;

    if (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD) {
      dragState.current.moved = true;
    }

    // bottom increases as we move up (negative dy), decreases as we move down
    const newBottom = dragState.current.startBottom - dy;
    // right increases as we move left (negative dx), decreases as we move right
    const newRight = dragState.current.startRight - dx;

    const clamped = clampPosition(newBottom, newRight);
    setPosition(clamped);
  }, []);

  const handlePointerUp = useCallback((e) => {
    if (!dragState.current) return;
    const wasMoved = dragState.current.moved;
    dragState.current = null;
    setDragging(false);

    if (!wasMoved && onClick) {
      onClick(e);
    } else if (wasMoved && storageKey) {
      // Persist final position
      setPosition((pos) => {
        try {
          localStorage.setItem(storageKey, JSON.stringify(pos));
        } catch (_) {}
        return pos;
      });
    }
  }, [onClick, storageKey]);

  const fabStyle = {
    position: 'fixed',
    bottom: position.bottom,
    right: position.right,
    zIndex: 9999,
    cursor: dragging ? 'grabbing' : 'grab',
    userSelect: 'none',
    touchAction: 'none',
    ...style,
  };

  return (
    <div
      className={className}
      style={fabStyle}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      {...rest}
    >
      {/* Small drag indicator dot */}
      {!dragging && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 2,
            left: 2,
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.5)',
            pointerEvents: 'none',
          }}
        />
      )}
      {children}
    </div>
  );
}

export default DraggableFab;
